import { type FormEvent, useMemo, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import {
  type PartCategory,
  useCreatePartCategory,
  useDeletePartCategory,
  usePartCategories,
  useUpdatePartCategory,
} from '@/lib/parts';

interface Props {
  orgSlug: string;
}

/**
 * Per-org part-category management. Reads the org's tree and lets
 * an admin/owner add children, rename, reparent, or delete any
 * row. Single-pane interface — no separate "edit" page; everything
 * is inline via expand/collapse and per-row controls.
 *
 * The tree is built client-side from the flat list (the server
 * returns rows ordered by sort_order). Cycle prevention is enforced
 * by the server; the move picker filters out self + descendants
 * client-side so the dropdown only offers valid targets.
 */
export function CategoriesPanel({ orgSlug }: Props) {
  const list = usePartCategories(orgSlug);
  const create = useCreatePartCategory(orgSlug);

  const [showRoot, setShowRoot] = useState(false);
  const [newRootName, setNewRootName] = useState('');

  // Set of expanded category ids; everything starts collapsed so
  // the panel doesn't dump 60 rows on first render.
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const tree = useMemo(() => buildTree(list.data ?? []), [list.data]);

  const onAddRoot = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!newRootName.trim()) return;
    await create.mutateAsync({ name: newRootName.trim(), parent_id: null });
    setNewRootName('');
    setShowRoot(false);
  };

  return (
    <section className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
      <div className="border-b border-stone-200 dark:border-stone-800 p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold">Categories</h2>
            <p className="text-sm text-stone-500 dark:text-stone-400">
              Organize parts into your own taxonomy. Each org has its
              own tree — renaming or deleting here only affects this org.
            </p>
          </div>
          {!showRoot && (
            <Button onClick={() => setShowRoot(true)}>
              + New top-level
            </Button>
          )}
        </div>

        {showRoot && (
          <form onSubmit={onAddRoot} className="mt-3 flex items-center gap-2">
            <Input
              value={newRootName}
              onChange={(e) => setNewRootName(e.target.value)}
              placeholder="e.g. Vintage TTL"
              autoFocus
              required
            />
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Adding…' : 'Add'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowRoot(false);
                setNewRootName('');
              }}
            >
              Cancel
            </Button>
          </form>
        )}
        {create.isError && (
          <p className="mt-2 text-sm text-red-600">
            {(create.error as Error).message}
          </p>
        )}
      </div>

      <div className="p-2">
        {list.isLoading && (
          <div className="flex items-center gap-2 p-3 text-sm text-stone-500">
            <Spinner /> Loading…
          </div>
        )}
        {list.isError && (
          <p className="p-3 text-sm text-red-600">
            Couldn't load categories: {(list.error as Error).message}
          </p>
        )}
        {tree.roots.length > 0 && (
          <ul className="space-y-0.5">
            {tree.roots.map((node) => (
              <CategoryNode
                key={node.id}
                orgSlug={orgSlug}
                node={node}
                tree={tree}
                expanded={expanded}
                setExpanded={setExpanded}
                allRows={list.data ?? []}
                depth={0}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

interface TreeNode extends PartCategory {
  children: TreeNode[];
}

interface Tree {
  roots: TreeNode[];
  byId: Map<number, TreeNode>;
}

function buildTree(rows: PartCategory[]): Tree {
  const byId = new Map<number, TreeNode>();
  for (const row of rows) byId.set(row.id, { ...row, children: [] });
  const roots: TreeNode[] = [];
  for (const node of byId.values()) {
    if (node.parent_id == null) {
      roots.push(node);
    } else {
      const parent = byId.get(node.parent_id);
      if (parent) parent.children.push(node);
      else roots.push(node);
    }
  }
  // Stable sort within each level by (sort_order, name).
  const sortLevel = (list: TreeNode[]) => {
    list.sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
    for (const c of list) sortLevel(c.children);
  };
  sortLevel(roots);
  return { roots, byId };
}

function descendantIds(node: TreeNode): Set<number> {
  const out = new Set<number>([node.id]);
  const walk = (n: TreeNode) => {
    for (const c of n.children) {
      out.add(c.id);
      walk(c);
    }
  };
  walk(node);
  return out;
}

interface NodeProps {
  orgSlug: string;
  node: TreeNode;
  tree: Tree;
  expanded: Set<number>;
  setExpanded: React.Dispatch<React.SetStateAction<Set<number>>>;
  allRows: PartCategory[];
  depth: number;
}

function CategoryNode({
  orgSlug,
  node,
  tree,
  expanded,
  setExpanded,
  allRows,
  depth,
}: NodeProps) {
  const isOpen = expanded.has(node.id);
  const hasChildren = node.children.length > 0;
  const [mode, setMode] = useState<'view' | 'rename' | 'addChild' | 'move' | 'delete'>('view');

  const toggleOpen = () => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(node.id)) next.delete(node.id);
      else next.add(node.id);
      return next;
    });
  };

  return (
    <li>
      <div
        className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-stone-50 dark:hover:bg-stone-800/50"
        style={{ paddingLeft: `${depth * 1.25 + 0.5}rem` }}
      >
        {hasChildren ? (
          <button
            onClick={toggleOpen}
            className="text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 w-4 text-center"
            aria-label={isOpen ? 'Collapse' : 'Expand'}
          >
            {isOpen ? '▾' : '▸'}
          </button>
        ) : (
          <span className="w-4" aria-hidden />
        )}
        <span className="flex-1 text-sm">
          <span className="font-medium">{node.name}</span>
          <span className="ml-2 text-xs text-stone-500">
            {(node.part_count ?? 0) > 0 && `${node.part_count} parts`}
            {(node.part_count ?? 0) > 0 && (node.child_count ?? 0) > 0 && ', '}
            {(node.child_count ?? 0) > 0 && `${node.child_count} children`}
          </span>
        </span>
        <div className="flex items-center gap-1">
          <Button className="!px-2 !py-1 text-xs" variant="secondary" onClick={() => setMode('addChild')}>
            + Add
          </Button>
          <Button className="!px-2 !py-1 text-xs" variant="secondary" onClick={() => setMode('rename')}>
            Rename
          </Button>
          <Button className="!px-2 !py-1 text-xs" variant="secondary" onClick={() => setMode('move')}>
            Move
          </Button>
          <Button className="!px-2 !py-1 text-xs" variant="danger" onClick={() => setMode('delete')}>
            Delete
          </Button>
        </div>
      </div>

      {mode === 'rename' && (
        <RenameForm
          orgSlug={orgSlug}
          node={node}
          depth={depth}
          onClose={() => setMode('view')}
        />
      )}
      {mode === 'addChild' && (
        <AddChildForm
          orgSlug={orgSlug}
          parent={node}
          depth={depth}
          onClose={() => {
            setMode('view');
            setExpanded((prev) => new Set(prev).add(node.id));
          }}
        />
      )}
      {mode === 'move' && (
        <MoveForm
          orgSlug={orgSlug}
          node={node}
          tree={tree}
          allRows={allRows}
          depth={depth}
          onClose={() => setMode('view')}
        />
      )}
      {mode === 'delete' && (
        <DeleteForm
          orgSlug={orgSlug}
          node={node}
          allRows={allRows}
          depth={depth}
          onClose={() => setMode('view')}
        />
      )}

      {hasChildren && isOpen && (
        <ul className="space-y-0.5">
          {node.children.map((child) => (
            <CategoryNode
              key={child.id}
              orgSlug={orgSlug}
              node={child}
              tree={tree}
              expanded={expanded}
              setExpanded={setExpanded}
              allRows={allRows}
              depth={depth + 1}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function RenameForm({
  orgSlug,
  node,
  depth,
  onClose,
}: {
  orgSlug: string;
  node: TreeNode;
  depth: number;
  onClose: () => void;
}) {
  const update = useUpdatePartCategory(orgSlug);
  const [name, setName] = useState(node.name);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim() || name.trim() === node.name) return onClose();
    await update.mutateAsync({ id: node.id, name: name.trim() });
    onClose();
  };
  return (
    <form
      onSubmit={submit}
      className="flex items-center gap-2 px-2 py-2"
      style={{ paddingLeft: `${depth * 1.25 + 1.75}rem` }}
    >
      <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus required />
      <Button type="submit" className="!px-2 !py-1 text-xs" disabled={update.isPending}>
        {update.isPending ? 'Saving…' : 'Save'}
      </Button>
      <Button type="button" className="!px-2 !py-1 text-xs" variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      {update.isError && (
        <span className="text-sm text-red-600">{(update.error as Error).message}</span>
      )}
    </form>
  );
}

function AddChildForm({
  orgSlug,
  parent,
  depth,
  onClose,
}: {
  orgSlug: string;
  parent: TreeNode;
  depth: number;
  onClose: () => void;
}) {
  const create = useCreatePartCategory(orgSlug);
  const [name, setName] = useState('');
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim()) return;
    await create.mutateAsync({ name: name.trim(), parent_id: parent.id });
    onClose();
  };
  return (
    <form
      onSubmit={submit}
      className="flex items-center gap-2 px-2 py-2"
      style={{ paddingLeft: `${depth * 1.25 + 1.75}rem` }}
    >
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={`New child of ${parent.name}`}
        autoFocus
        required
      />
      <Button type="submit" className="!px-2 !py-1 text-xs" disabled={create.isPending}>
        {create.isPending ? 'Adding…' : 'Add'}
      </Button>
      <Button type="button" className="!px-2 !py-1 text-xs" variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      {create.isError && (
        <span className="text-sm text-red-600">{(create.error as Error).message}</span>
      )}
    </form>
  );
}

function MoveForm({
  orgSlug,
  node,
  tree,
  allRows,
  depth,
  onClose,
}: {
  orgSlug: string;
  node: TreeNode;
  tree: Tree;
  allRows: PartCategory[];
  depth: number;
  onClose: () => void;
}) {
  const update = useUpdatePartCategory(orgSlug);
  const blocked = descendantIds(node);
  const candidates = allRows
    .filter((r) => !blocked.has(r.id))
    .sort((a, b) => a.name.localeCompare(b.name));
  const [target, setTarget] = useState<string>(
    node.parent_id == null ? '__root__' : String(node.parent_id),
  );
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const parent_id = target === '__root__' ? null : Number(target);
    await update.mutateAsync({ id: node.id, parent_id });
    onClose();
  };
  return (
    <form
      onSubmit={submit}
      className="flex flex-wrap items-center gap-2 px-2 py-2"
      style={{ paddingLeft: `${depth * 1.25 + 1.75}rem` }}
    >
      <span className="text-sm text-stone-600 dark:text-stone-400">
        Move under:
      </span>
      <select
        value={target}
        onChange={(e) => setTarget(e.target.value)}
        className="rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2 py-1 text-sm"
      >
        <option value="__root__">— top level —</option>
        {candidates.map((c) => (
          <option key={c.id} value={c.id}>
            {pathDisplay(c, tree)}
          </option>
        ))}
      </select>
      <Button type="submit" className="!px-2 !py-1 text-xs" disabled={update.isPending}>
        {update.isPending ? 'Saving…' : 'Move'}
      </Button>
      <Button type="button" className="!px-2 !py-1 text-xs" variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      {update.isError && (
        <span className="text-sm text-red-600">{(update.error as Error).message}</span>
      )}
    </form>
  );
}

function DeleteForm({
  orgSlug,
  node,
  allRows,
  depth,
  onClose,
}: {
  orgSlug: string;
  node: TreeNode;
  allRows: PartCategory[];
  depth: number;
  onClose: () => void;
}) {
  const del = useDeletePartCategory(orgSlug);
  const otherRows = allRows.filter((r) => r.id !== node.id);
  const [reassign, setReassign] = useState<'orphan' | 'reassign'>('orphan');
  const [reassignTarget, setReassignTarget] = useState<string>(
    otherRows[0] ? String(otherRows[0].id) : '',
  );

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (reassign === 'orphan') {
      await del.mutateAsync({ id: node.id });
    } else {
      const target = Number(reassignTarget);
      await del.mutateAsync({
        id: node.id,
        reassign_children_to: target,
        reassign_parts_to: target,
      });
    }
    onClose();
  };

  return (
    <form
      onSubmit={submit}
      className="border-l-2 border-red-300 dark:border-red-800 ml-2 mb-1 px-3 py-2 space-y-2"
      style={{ marginLeft: `${depth * 1.25 + 1.5}rem` }}
    >
      <p className="text-sm">
        Delete <span className="font-medium">{node.name}</span>?{' '}
        {(node.part_count ?? 0) > 0 || (node.child_count ?? 0) > 0
          ? `(${node.part_count ?? 0} parts and ${node.child_count ?? 0} children will need to go somewhere.)`
          : 'No parts or children — safe to delete.'}
      </p>
      {((node.part_count ?? 0) > 0 || (node.child_count ?? 0) > 0) && (
        <fieldset className="space-y-1.5">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              checked={reassign === 'orphan'}
              onChange={() => setReassign('orphan')}
            />
            <span>
              Just delete — parts become uncategorized, children become root.
            </span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              checked={reassign === 'reassign'}
              onChange={() => setReassign('reassign')}
            />
            <span>Move parts and children to:</span>
            <select
              value={reassignTarget}
              onChange={(e) => setReassignTarget(e.target.value)}
              disabled={reassign !== 'reassign'}
              className="rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2 py-1 text-sm"
            >
              {otherRows.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </label>
        </fieldset>
      )}
      <div className="flex items-center gap-2">
        <Button type="submit" className="!px-2 !py-1 text-xs" variant="danger" disabled={del.isPending}>
          {del.isPending ? 'Deleting…' : 'Delete'}
        </Button>
        <Button type="button" className="!px-2 !py-1 text-xs" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        {del.isError && (
          <span className="text-sm text-red-600">{(del.error as Error).message}</span>
        )}
      </div>
    </form>
  );
}

function pathDisplay(node: PartCategory, tree: Tree): string {
  const parts: string[] = [node.name];
  let cur: PartCategory | undefined = node;
  while (cur && cur.parent_id != null) {
    const parent = tree.byId.get(cur.parent_id);
    if (!parent) break;
    parts.unshift(parent.name);
    cur = parent;
  }
  return parts.join(' › ');
}
